import React from "react";
import { Sparkles } from "lucide-react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { createBackgroundStyles } from "../../../../styles/masterStyles";

const AVAILABLE_METAMAGICS = [
  {
    name: "Careful Spell",
    cost: "1 sorcery point",
    description:
      "When you cast a spell that forces other creatures to make a saving throw, you can protect some of those creatures from the spell's full force. Choose a number of those creatures up to your Charisma modifier (minimum of one creature). A chosen creature automatically succeeds on its saving throw against the spell.",
    preview: "Protect allies from spell effects",
  },
  {
    name: "Distant Spell",
    cost: "1 sorcery point",
    description:
      "When you cast a spell that has a range of 5 feet or greater, you can double the range of the spell. When you cast a spell that has a range of touch, you can make the range of the spell 30 feet.",
    preview: "Double spell range or make touch spells 30 feet",
  },
  {
    name: "Empowered Spell",
    cost: "1 sorcery point",
    description:
      "When you roll damage for a spell, you can reroll a number of the damage dice up to your Charisma modifier (minimum of one). You must use the new rolls.",
    preview: "Reroll damage dice for better results",
  },
  {
    name: "Extended Spell",
    cost: "1 sorcery point",
    description:
      "When you cast a spell that has a duration of 1 minute or longer, you can double its duration, to a maximum duration of 24 hours.",
    preview: "Double spell duration up to 24 hours",
  },
  {
    name: "Heightened Spell",
    cost: "3 sorcery points",
    description:
      "When you cast a spell that forces a creature to make a saving throw to resist its effects, you can give one target of the spell disadvantage on its first saving throw made against the spell.",
    preview: "Give one target disadvantage on saves",
  },
  {
    name: "Quickened Spell",
    cost: "2 sorcery points",
    description:
      "When you cast a spell that has a casting time of 1 action, you can change the casting time to 1 bonus action for this casting.",
    preview: "Cast action spells as bonus actions",
  },
  {
    name: "Subtle Spell",
    cost: "1 sorcery point",
    description:
      "When you cast a spell, you can cast it without any somatic or verbal components.",
    preview: "Cast spells without verbal or somatic components",
  },
  {
    name: "Twinned Spell",
    cost: "Varies (spell level)",
    description:
      "When you cast a spell that targets only one creature and doesn't have a range of self, you can target a second creature in range with the same spell. The cost equals the spell's level (1 sorcery point for cantrips).",
    preview: "Target a second creature with single-target spells",
  },
];

const CASTING_STYLE_METAMAGICS = {
  "Willpower Caster": [
    {
      name: "Fierce Spell",
      cost: "2/4 sorcery points",
      description:
        "When you cast a spell, you can spend 2 sorcery points to cast that spell as if it were cast using a spell slot one level higher than its original level, or 4 sorcery points to cast that spell two levels higher. The spell's higher level cannot exceed your highest available level of spell slots. This does not count against your number of Metamagic options.",
      preview: "Cast spells at higher levels",
      requiredLevel: 3,
      castingStyle: "Willpower Caster",
    },
    {
      name: "Resistant Spell",
      cost: "1 sorcery point per level",
      description:
        "When you cast a spell, you can spend 1 sorcery point per increased level to make your spell be treated by spell deflection, finite incantatem, reparifarge, or langlock as if your spell was cast using a spell slot higher than its original level, making your spell more resistant. The spell's higher level cannot exceed your highest available level of spell slots. This does not count against your number of Metamagic options.",
      preview: "Make spells more resistant to dispelling",
      requiredLevel: 3,
      castingStyle: "Willpower Caster",
    },
  ],
  "Technique Caster": [
    {
      name: "Bouncing Spell",
      cost: "2 sorcery points",
      description:
        "When a creature succeeds at a saving throw against a single-target spell you cast, you can spend 2 Sorcery Points to have the spell bounce, targeting another creature of your choice within 30 ft. of the original target without spending another spell slot or taking an additional action.",
      preview: "Redirect spells that miss to new targets",
      requiredLevel: 3,
      castingStyle: "Technique Caster",
    },
    {
      name: "Maximized Spell",
      cost: "2x spell level sorcery points",
      description:
        "When you roll damage for a leveled spell, you can spend a number of Sorcery Points equal to twice the spell's level to deal maximum damage to one target of the spell. Maximized spell can not be applied to Exploit Weakness damage.",
      preview: "Deal maximum damage with spells",
      requiredLevel: 3,
      castingStyle: "Technique Caster",
    },
    {
      name: "Seeking Spell",
      cost: "2 sorcery points",
      description:
        "If you make an attack roll for a spell and miss, you can spend 2 Sorcery Points to reroll the d20, and you must use the new roll. You can use Seeking Spell even if you have already used a different Metamagic option during the casting of the spell.",
      preview: "Reroll missed spell attacks",
      requiredLevel: 3,
      castingStyle: "Technique Caster",
    },
  ],
};

const MetaMagicSection = ({ character, onChange, disabled = false }) => {
  const { theme } = useTheme();
  const styles = createBackgroundStyles(theme);

  const getMetaMagicChoices = (character) => {
    if (
      character?.metamagicChoices &&
      typeof character.metamagicChoices === "object"
    ) {
      return Object.keys(character.metamagicChoices).filter(
        (key) => character.metamagicChoices[key] === true
      );
    }

    if (
      character?.metamagic_choices &&
      typeof character.metamagic_choices === "object"
    ) {
      return Object.keys(character.metamagic_choices).filter(
        (key) => character.metamagic_choices[key] === true
      );
    }

    return (
      character?.metamagic ||
      character?.metaMagic ||
      character?.meta_magic ||
      []
    );
  };

  const currentMetaMagics = getMetaMagicChoices(character);

  const getAvailableMetaMagics = (character) => {
    const baseMetaMagics = [...AVAILABLE_METAMAGICS];
    const castingStyle = character?.castingStyle;
    const level = character?.level || 1;

    if (castingStyle && CASTING_STYLE_METAMAGICS[castingStyle]) {
      const castingStyleMetaMagics = CASTING_STYLE_METAMAGICS[
        castingStyle
      ].filter((mm) => level >= (mm.requiredLevel || 1));
      baseMetaMagics.push(...castingStyleMetaMagics);
    }

    return baseMetaMagics;
  };

  const getAutomaticMetaMagics = (character) => {
    const automaticMetaMagics = [];
    const castingStyle = character?.castingStyle;
    const level = character?.level || 1;

    if (castingStyle === "Willpower Caster" && level >= 3) {
      const willpowerMetaMagics = CASTING_STYLE_METAMAGICS["Willpower Caster"];
      automaticMetaMagics.push(...willpowerMetaMagics);
    }

    return automaticMetaMagics;
  };

  const allAvailableMetaMagics = getAvailableMetaMagics(character);
  const automaticMetaMagics = getAutomaticMetaMagics(character);
  const selectedCount = currentMetaMagics.length;

  const handleMetaMagicToggle = (metaMagicName) => {
    const isSelected = currentMetaMagics.includes(metaMagicName);
    let updatedMetaMagics;

    if (isSelected) {
      updatedMetaMagics = currentMetaMagics.filter(
        (mm) => mm !== metaMagicName
      );
    } else {
      updatedMetaMagics = [...currentMetaMagics, metaMagicName];
    }

    const metamagicChoices = {};
    updatedMetaMagics.forEach((name) => {
      metamagicChoices[name] = true;
    });

    onChange("metamagicChoices", metamagicChoices);
  };

  const selectedMetaMagics = allAvailableMetaMagics.filter((mm) =>
    currentMetaMagics.includes(mm.name)
  );

  const availableMetaMagics = allAvailableMetaMagics.filter(
    (mm) => !currentMetaMagics.includes(mm.name)
  );

  return (
    <div style={styles.sectionContainer}>
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: theme.background,
          border: `1px solid ${theme.border}`,
          borderRadius: "8px",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "4px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Sparkles size={16} color={theme.primary} />
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: theme.text,
              }}
            >
              Options
            </span>
          </div>
          <span
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: theme.primary,
            }}
          >
            {selectedCount} selected
            {automaticMetaMagics.length > 0 &&
              ` + ${automaticMetaMagics.length} automatic`}
          </span>
        </div>
        <div
          style={{
            fontSize: "12px",
            color: theme.textSecondary,
          }}
        >
          Select as many Metamagic options as allowed by the rulebook{" "}
        </div>
      </div>

      {automaticMetaMagics.length > 0 && (
        <div style={styles.selectedElementsSection}>
          <div
            style={{
              ...styles.availableSectionHeader,
              backgroundColor: "#8b5cf615",
              border: `1px solid #8b5cf6`,
              marginBottom: "12px",
            }}
          >
            <span style={{ ...styles.availableSectionTitle, color: "#8b5cf6" }}>
              Automatic Metamagics (from {character?.castingStyle})
            </span>
          </div>
          {automaticMetaMagics.map((metaMagic) => {
            return (
              <div
                key={metaMagic.name}
                style={{
                  ...styles.selectedElementCard,
                  border: `2px solid #8b5cf6`,
                  backgroundColor: "#8b5cf615",
                }}
              >
                <div style={styles.featHeader}>
                  <div
                    style={{ ...styles.featLabelClickable, cursor: "default" }}
                  >
                    <span style={styles.featNameSelected}>
                      {metaMagic.name}
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "10px",
                          fontWeight: "600",
                          color: "#8b5cf6",
                          backgroundColor: "#8b5cf625",
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}
                      >
                        Granted at level {metaMagic.requiredLevel || 3}
                      </span>
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#8b5cf6",
                          backgroundColor: "#8b5cf625",
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}
                      >
                        {metaMagic.cost}
                      </span>
                    </span>
                  </div>
                </div>

                <div style={styles.featPreviewSelected}>
                  {metaMagic.preview}
                </div>

                <div style={styles.featDescriptionSelected}>
                  <p
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.5",
                      margin: "0",
                      color: theme.text,
                    }}
                  >
                    {metaMagic.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedMetaMagics.length > 0 && (
        <div style={styles.selectedElementsSection}>
          <div style={styles.availableSectionHeader}>
            <span style={styles.availableSectionTitle}>
              Selected Metamagics
            </span>
          </div>
          {selectedMetaMagics.map((metaMagic) => {
            return (
              <div key={metaMagic.name} style={styles.selectedElementCard}>
                <div style={styles.featHeader}>
                  <label style={styles.featLabelClickable}>
                    <input
                      type="checkbox"
                      name="metamagic"
                      checked={true}
                      onChange={() => handleMetaMagicToggle(metaMagic.name)}
                      disabled={disabled}
                      style={{
                        width: "18px",
                        height: "18px",
                        marginRight: "8px",
                        cursor: disabled ? "not-allowed" : "pointer",
                        accentColor: theme.success,
                        transform: "scale(1.2)",
                      }}
                    />
                    <span style={styles.featNameSelected}>
                      {metaMagic.name}
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "12px",
                          fontWeight: "500",
                          color: theme.primary,
                          backgroundColor: `${theme.primary}15`,
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}
                      >
                        {metaMagic.cost}
                      </span>
                    </span>
                  </label>
                </div>

                <div style={styles.featPreviewSelected}>
                  {metaMagic.preview}
                </div>

                <div style={styles.featDescriptionSelected}>
                  <p
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.5",
                      margin: "0",
                      color: theme.text,
                    }}
                  >
                    {metaMagic.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {availableMetaMagics.length > 0 && (
        <div style={styles.availableElementsSection}>
          <div style={styles.availableSectionHeader}>
            <span style={styles.availableSectionTitle}>
              Available Metamagic Options
            </span>
          </div>

          <div style={styles.availableElementsContainer}>
            {availableMetaMagics.map((metaMagic) => {
              const isDisabled = disabled;

              return (
                <div key={metaMagic.name} style={styles.featCard}>
                  <div style={styles.featHeader}>
                    <label style={styles.featLabelClickable}>
                      <input
                        type="checkbox"
                        name="metamagic"
                        checked={false}
                        onChange={() => handleMetaMagicToggle(metaMagic.name)}
                        disabled={isDisabled}
                        style={{
                          width: "18px",
                          height: "18px",
                          marginRight: "8px",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          accentColor: theme.primary,
                          transform: "scale(1.2)",
                          opacity: isDisabled ? 0.5 : 1,
                        }}
                      />
                      <span
                        style={{
                          ...styles.featName,
                          opacity: isDisabled ? 0.5 : 1,
                        }}
                      >
                        {metaMagic.name}
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "12px",
                            fontWeight: "500",
                            color: isDisabled
                              ? theme.textSecondary
                              : theme.primary,
                            backgroundColor: isDisabled
                              ? `${theme.textSecondary}15`
                              : `${theme.primary}15`,
                            padding: "2px 6px",
                            borderRadius: "4px",
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          {metaMagic.cost}
                        </span>
                      </span>
                    </label>
                  </div>

                  <div
                    style={{
                      ...styles.featPreview,
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    {metaMagic.preview}
                  </div>

                  <div
                    style={{
                      ...styles.featDescription,
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.5",
                        margin: "0",
                        color: theme.text,
                      }}
                    >
                      {metaMagic.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {availableMetaMagics.length === 0 && selectedMetaMagics.length === 0 && (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: theme.textSecondary,
            fontStyle: "italic",
            backgroundColor: theme.background,
            border: `1px dashed ${theme.border}`,
            borderRadius: "12px",
          }}
        >
          <Sparkles size={24} style={{ opacity: 0.5, marginBottom: "8px" }} />
          <div>No metamagic options available</div>
        </div>
      )}
    </div>
  );
};

export default MetaMagicSection;
